import { ProcedureType } from "api/type";
import { ThemeContext } from "contexts";
import React, { useContext } from "react";
import { formatProcedureName } from "utils/formatProcedureName";
import { hexToRgba } from "utils/hexToRgba";
import NormalText from "../NormalText";
import { procedureOptions, TSelectOption } from "./pickProcedureData";

type PickProcedureProps = {
  initialProcedure?: ProcedureType | null;
  allowedProcedures: ProcedureType[];
  aiProcedures: ProcedureType[];
  onProcedureSelect: (procedure: ProcedureType) => void;
  gap?: number;
};

const PickProcedure: React.FC<PickProcedureProps> = ({
  initialProcedure = null,
  allowedProcedures = [],
  aiProcedures = [],
  onProcedureSelect = () => {},
  gap = 24,
}) => {
  const theme = useContext(ThemeContext);
  const [selectedProcedure, setSelectedProcedure] =
    React.useState<ProcedureType | null>(initialProcedure);
  const [showableProcedure, setShowableProcedure] = React.useState<
    TSelectOption[] | null
  >(null);

  const handleProcedureClick = (procedure: ProcedureType) => {
    setSelectedProcedure(procedure);
    onProcedureSelect(procedure);
  };

  React.useEffect(() => {
    const showableProcedure = procedureOptions.filter((option) =>
      allowedProcedures.includes(option.procedure),
    );
    setShowableProcedure(showableProcedure);
  }, [allowedProcedures]);

  if (allowedProcedures.length === 0)
    return (
      <NormalText
        textContent={
          "Clinic doesn't support any procedures for your selected areas."
        }
        style={{
          flex: 1,
          margin: "auto",
          textAlign: "center",
          color: "#ff0000",
        }}
      />
    );

  if (showableProcedure === null) return null;

  if (showableProcedure.length === 0) {
    return (
      <NormalText
        textContent={
          "No procedures available for the selected areas. Please select different areas."
        }
        style={{
          flex: 1,
        }}
      />
    );
  }

  return (
    <div className="remove_scroll_bar" style={styles.container}>
      {showableProcedure.map((option) => (
        <div
          key={option.procedure}
          onClick={() => handleProcedureClick(option.procedure)}
          style={{
            ...styles.option,
            marginBottom: gap,
            background:
              selectedProcedure === option.procedure
                ? `${hexToRgba(theme?.themeColor || "#00B7F4", 0.1)}`
                : "transparent",
          }}
        >
          <img alt="" src={option.procedureImage} style={styles.image} />
          <NormalText
            textContent={formatProcedureName(option.procedure)}
            style={{ fontSize: 18, fontWeight: 600 }}
          />
          {aiProcedures.includes(option.procedure) && (
            <div style={{ position: "relative" }}>
              <svg
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.1713 17H4.20866C3.50141 17 2.82314 16.719 2.32304 16.219C1.82294 15.7189 1.54199 15.0406 1.54199 14.3333V3.66667C1.54199 2.95942 1.82294 2.28115 2.32304 1.78105C2.82314 1.28095 3.50141 1 4.20866 1H14.8753C15.5826 1 16.2608 1.28095 16.7609 1.78105C17.261 2.28115 17.542 2.95942 17.542 3.66667V9.44444"
                  stroke="black"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.16406 11.9839V7.92042C6.16406 7.38157 6.37812 6.86478 6.75915 6.48376C7.14017 6.10273 7.65696 5.88867 8.19581 5.88867C8.73466 5.88867 9.25144 6.10273 9.63247 6.48376C10.0135 6.86478 10.2276 7.38157 10.2276 7.92042V11.9839M6.16406 9.95216H10.2276M13.2752 5.88867V11.9839"
                  stroke="black"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div style={{ position: "absolute", bottom: -3, right: -4 }}>
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_7128_3147)">
                    <path
                      d="M7.9313 3.98421L7.93098 3.98409L5.97042 3.27144L5.25777 1.31088L5.25765 1.31056C5.21858 1.2053 5.14823 1.11452 5.05606 1.05042C4.96388 0.986314 4.8543 0.951953 4.74202 0.951953C4.62975 0.951953 4.52016 0.986314 4.42799 1.05042C4.33581 1.11452 4.26547 1.2053 4.2264 1.31056L4.22628 1.31088L3.51363 3.27144L1.55307 3.98409L1.55275 3.98421C1.44749 4.02328 1.35671 4.09363 1.29261 4.1858C1.2285 4.27798 1.19414 4.38756 1.19414 4.49984C1.19414 4.61211 1.2285 4.72169 1.29261 4.81387C1.35671 4.90604 1.44749 4.97639 1.55275 5.01546L1.55306 5.01557L3.51363 5.72854L4.22628 7.68879L4.2264 7.68911C4.26547 7.79437 4.33581 7.88515 4.42799 7.94925C4.52016 8.01336 4.62975 8.04772 4.74202 8.04772C4.8543 8.04772 4.96388 8.01336 5.05606 7.94925C5.14823 7.88515 5.21858 7.79437 5.25765 7.68911L5.25776 7.6888L5.97073 5.72823L7.93098 5.01558L7.9313 5.01546C8.03655 4.97639 8.12733 4.90604 8.19144 4.81387C8.25554 4.72169 8.28991 4.61211 8.28991 4.49984C8.28991 4.38756 8.25554 4.27798 8.19144 4.1858C8.12733 4.09363 8.03655 4.02328 7.9313 3.98421ZM5.63368 5.21222L5.63367 5.21223C5.59251 5.22721 5.55514 5.25102 5.52418 5.28199C5.49321 5.31296 5.4694 5.35033 5.45442 5.39148L5.45441 5.3915L4.74202 7.35041L4.02964 5.3915L4.02963 5.39148C4.01465 5.35033 3.99083 5.31296 3.95987 5.28199C3.9289 5.25102 3.89153 5.22721 3.85038 5.21223L3.85036 5.21222L1.89145 4.49984L3.85036 3.78745L3.85038 3.78744C3.89153 3.77246 3.9289 3.74865 3.95987 3.71768C3.99083 3.68671 4.01465 3.64934 4.02963 3.60819L4.02964 3.60817L4.74202 1.64926L5.45441 3.60817L5.45442 3.60819C5.4694 3.64934 5.49321 3.68671 5.52418 3.71768C5.55514 3.74865 5.59251 3.77246 5.63367 3.78744L5.63368 3.78745L7.5926 4.49984L5.63368 5.21222Z"
                      fill="black"
                      stroke="black"
                      strokeWidth="0.1"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_7128_3147">
                      <rect
                        width="8"
                        height="8"
                        fill="white"
                        transform="translate(0.742188 0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    width: "100%",
    overflow: "auto",
  },
  option: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    cursor: "pointer",
    padding: "12px 16px",
    borderRadius: 20,
    border: "1px solid #DDDEE0",
  },
  image: {
    width: 20,
    height: 20,
    padding: 10,
    background: "#f3f3f3",
    borderRadius: "50%",
  },
} as const;

export default PickProcedure;
